import { getFileExtension } from '@/constants/ingest';
import useLimits from '@/hooks/useLimits';
import { useMultipartUpload } from '@/hooks/useMultipartUpload';
import type { UploadState } from '@/hooks/useMultipartUpload';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import * as Sentry from '@sentry/nextjs';
import { atom, useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useUpgrade } from '../Modals/UpgradeModal';
import { useCreateChatbot } from './useCreateChatbot';

const uploadProgressAtom = atom<Record<string, UploadState>>({});

export const useAudioProgressState = (chatbotId: string) => {
  const [uploadStates] = useAtom(uploadProgressAtom);
  return uploadStates[chatbotId];
};

const useIngestCustomAudio = () => {
  const { track } = useAnalytics();
  const { exceededLimits, optimisticIncreaseImportLimit } = useLimits();
  const { showUpgrade } = useUpgrade();
  const router = useRouter();
  const [, setUploadStates] = useAtom(uploadProgressAtom);
  const { uploadFile } = useMultipartUpload();

  const { createChatbot } = useCreateChatbot();

  const { mutateAsync: updateChatbotUrl } =
    api.file.updateChatbotUrl.useMutation();

  const ingestCustomAudio = async (files: File[], rerouting = true) => {
    try {
      const file = files[0];

      if (exceededLimits?.isImportLimitExceeded) {
        showUpgrade('importLimit');
        track('importLimitHit');
        track('Paywall Shown', { reason: 'Import Limit Hit' });
        return;
      }

      const chatbot = await createChatbot({
        name: file.name,
        type: 'AUDIO',
      });
      if (!chatbot) return;

      optimisticIncreaseImportLimit();

      await uploadFile(file, {
        chatbotId: chatbot.id,
        type: 'audio',
        onError: (error) => {
          Sentry.captureException(error);
        },
        onProgress: (state) => {
          setUploadStates((prev) => ({
            ...prev,
            [chatbot.id]: state,
          }));
        },
      });

      await updateChatbotUrl({ chatbotId: chatbot.id, type: 'AUDIO' });

      const extension = getFileExtension(file.type);
      track('chatbotCreated', {
        chatbotId: chatbot.id,
        fileExtension: extension,
        type: 'AUDIO',
      });

      if (rerouting) {
        router.push(`/b/${chatbot.id}`);
        return null;
      }

      return chatbot.id;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  };

  return { ingestCustomAudio };
};

export default useIngestCustomAudio;
